// services/appointments.service.js
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import { generateClient } from '@aws-amplify/api';
import { updateSlot, getSlot } from './slots.service';
import { updateStatus, setNeedsAlternateTime } from './submissions.service';
import { sendAppointmentNotification, clearAppointmentConfirmationFlag } from './notifications.service';
import { createAppointment,updateAppointment } from '../graphql/mutations';
const client = generateClient();

// Statuses that should NOT be changed when scheduling an appointment
const PROTECTED_STATUSES = ['Assessment InProcess', 'Assessment Completed'];


export const fetchAllAppointments = async () => {
  // Get today's date for filtering
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  
  let allItems = [];
  let nextToken = null;
  
  // Fetch all appointments with pagination
  do {
    const result = await client.graphql({
      query: queries.listAppointments,
      variables: {
        limit: 1000, // Maximum limit
        nextToken: nextToken
      }
    });

    const items = result.data.listAppointments.items || [];
    allItems = allItems.concat(items);
    nextToken = result.data.listAppointments.nextToken;
  } while (nextToken);

  // Filter client-side for today and future appointments, excluding cancelled ones
  return allItems.filter((appt) => {
    // Skip cancelled appointments
    if (appt.status === 'CANCELED') return false;
    
    // Check if appointment slot date is today or in the future
    if (appt.slot && appt.slot.date) {
      return appt.slot.date >= today;
    }
    
    // If no slot data, include the appointment (fallback)
    return true;
  });
};

export const fetchAppointmentsBySubmission = async (submissionId) => {
    const result = await client.graphql({
        query: queries.appointmentsBySubmissionId,
        variables: {
          submissionId
        }
      });
      
      const items = result.data.appointmentsBySubmissionId.items || [];

      const sorted = items.sort((a, b) =>
        new Date(b.bookedAt) - new Date(a.bookedAt)
      );
    
      return sorted;
};

export const updateAppointmentRecord = async (input) => {
  const result = await client.graphql({
    query: updateAppointment,
    variables: { input }
  });

  const updatedAppointment = result?.data?.updateAppointment;

  if (updatedAppointment?.slotId) {
    try {
      const updateSlotInput = {
        id: updatedAppointment.slotId,
        isBooked: input.status === 'CANCELED' ? false : true,
      };

      const updateSlotResult = await updateSlot(updateSlotInput);
      console.log(`🟢 Slot updated to ${updateSlotInput.isBooked ? 'booked' : 'open'}:`, updateSlotResult);

      // Clear awaitingAppointmentConfirmation flag when appointment is canceled
      if (input.status === 'CANCELED' && updatedAppointment?.submissionId) {
        try {
          await clearAppointmentConfirmationFlag(updatedAppointment.submissionId);
          console.log('🟢 Cleared appointment confirmation flag for submission:', updatedAppointment.submissionId);
        } catch (flagErr) {
          console.error('⚠️ Failed to clear appointment confirmation flag:', flagErr);
        }
      }

      // Update submission status to 'Assessment Completed' when appointment is completed
      if (input.status === 'COMPLETED' && updatedAppointment?.submissionId) {
        try {
          await updateStatus(updatedAppointment.submissionId, 'Assessment Completed', 'System');
          console.log('✅ Submission status updated to "Assessment Completed"');
        } catch (statusErr) {
          console.error('⚠️ Failed to update submission status to Assessment Completed:', statusErr);
        }
      }
    } catch (err) {
      console.error('❌ Failed to update slot availability after appointment status change:', err);
    }
  }

  return updatedAppointment;
};

export const createAppointmentRecord = async (input) => {
   // console.log("createAppointmentRecord payload:", input);    
   // console.log('GraphQL mutation string (createAppointment):', createAppointment);

    // Guard: prevent booking slots in the past
    const slot = await getSlot(input.slotId);
    if (slot) {
      const now = new Date();
      const slotDateTime = new Date(`${slot.date}T${slot.startTime}`);
      if (slotDateTime <= now) {
        throw new Error('Cannot book an appointment in the past. Please select a future time slot.');
      }
    }

    const result = await client.graphql({
        query: createAppointment,
        variables: { input:  input  }
    });    

    if (result && result.data && result.data.createAppointment) {
      const updateSlotInput = {
        id: input.slotId,
        isBooked: true,
      };

      const updateSlotResult = await updateSlot(updateSlotInput);
      console.log('Slot updated:', updateSlotResult);

      // Update submission status to 'Assessment Scheduled' if not in protected status
      if (input.submissionId) {
        try {
          const submissionResult = await client.graphql({
            query: queries.getSubmission,
            variables: { id: input.submissionId }
          });
          const currentStatus = submissionResult?.data?.getSubmission?.Status;
          
          if (!PROTECTED_STATUSES.includes(currentStatus)) {
            await updateStatus(input.submissionId, 'Assessment Scheduled', 'System');
            console.log(`✅ Submission status updated to 'Assessment Scheduled' (previous: ${currentStatus})`);
          } else {
            console.log(`ℹ️ Submission status not changed - current status '${currentStatus}' is protected`);
          }
        } catch (statusError) {
          console.error('⚠️ Failed to update submission status:', statusError);
        }

        // Clear the "needs alternate time" flag now that an appointment is booked
        try {
          await setNeedsAlternateTime(input.submissionId, false, 'System');
          console.log('✅ Cleared needsAlternateTime flag after booking');
        } catch (flagErr) {
          console.error('⚠️ Failed to clear needsAlternateTime flag:', flagErr);
        }
      }
    } else {
      console.error('Appointment creation successful, but no data returned. Slot not updated.');
      // Optionally, you might want to handle this edge case (e.g., show an error to the user)
    }    
  return result.data.createAppointment;
};

/**
 * Preview: For all submissions with "Assessment Completed" status,
 * find appointments NOT in COMPLETED status. Returns list of impacted records (read-only).
 */
export const previewBackfillAppointmentStatuses = async () => {
  const preview = { checked: 0, impacted: [], skipped: [], errors: [] };

  try {
    let allSubmissions = [];
    let nextToken = null;
    do {
      const result = await client.graphql({
        query: queries.listSubmissions,
        variables: {
          filter: { Status: { eq: 'Assessment Completed' } },
          limit: 1000,
          nextToken
        }
      });
      allSubmissions = [...allSubmissions, ...result.data.listSubmissions.items];
      nextToken = result.data.listSubmissions.nextToken;
    } while (nextToken);

    console.log(`📋 Found ${allSubmissions.length} submissions with "Assessment Completed" status`);

    for (const submission of allSubmissions) {
      preview.checked++;
      try {
        const appointments = await fetchAppointmentsBySubmission(submission.id);
        const nonCompleted = appointments.filter(a => a.status === 'SCHEDULED');

        if (nonCompleted.length === 0) {
          preview.skipped.push({
            submissionId: submission.id,
            mobile: submission.mobilenumber,
            reason: 'No SCHEDULED appointments found'
          });
          continue;
        }

        // Pick the appointment closest to the submission status change (updatedAt)
        const statusChangedAt = new Date(submission.updatedAt).getTime();
        const closest = nonCompleted.reduce((best, appt) => {
          const apptTime = new Date(appt.bookedAt || appt.createdAt).getTime();
          const bestTime = new Date(best.bookedAt || best.createdAt).getTime();
          return Math.abs(apptTime - statusChangedAt) < Math.abs(bestTime - statusChangedAt) ? appt : best;
        });

        preview.impacted.push({
          submissionId: submission.id,
          mobile: submission.mobilenumber,
          appointmentId: closest.id,
          currentStatus: closest.status,
          slotDate: closest.slot?.date,
          slotTime: closest.slot?.startTime,
          bookedAt: closest.bookedAt
        });
      } catch (fetchErr) {
        preview.errors.push({
          submissionId: submission.id,
          error: fetchErr.message
        });
      }
    }
  } catch (err) {
    preview.errors.push({ error: err.message });
  }

  return preview;
};

/**
 * Execute backfill: Update the provided list of appointments to COMPLETED.
 * Accepts the impacted array from previewBackfillAppointmentStatuses.
 */
export const executeBackfillAppointmentStatuses = async (impactedItems) => {
  const report = { updated: [], errors: [] };

  for (const item of impactedItems) {
    try {
      await client.graphql({
        query: updateAppointment,
        variables: { input: { id: item.appointmentId, status: 'COMPLETED' } }
      });
      report.updated.push(item);
      console.log(`✅ Updated appointment ${item.appointmentId} to COMPLETED (submission: ${item.submissionId})`);
    } catch (updateErr) {
      report.errors.push({
        ...item,
        error: updateErr.message
      });
    }
  }

  console.log('📊 Backfill Report:', JSON.stringify(report, null, 2));
  return report;
};

export const deleteAppointmentRecord = async (id) => {
    const result = await client.graphql({
        query: mutations.deleteAppointment,
        variables: { input: { id } }
      });
  return result.data.deleteAppointment;
};
